import { useIsFetching, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useId, useState } from "react";
import { z } from "zod";

import {
	PEOPLE_QUERY_MAX_LENGTH,
	PERSON_STATUSES,
	personLabelKey,
	type AddPersonRequest,
	type PeopleFilter,
	type Person,
	type PersonGroup,
	type PersonStatus,
} from "@ravnar/api-contract";

import { ApiError } from "../../api/client";
import { addPerson, peoplePageQueryOptions, rolesAndTeamsQueryOptions } from "../../api/people";
import { useAskAgain } from "../../api/refresh";
import { sessionQueryOptions, useSession } from "../../api/session";
import { CloseIcon } from "../../components/icons";
import { PageHeader } from "../../components/shell/app-shell";
import { Avatar } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import { buttonClassName } from "../../components/ui/button-styles";
import { Card, CardHeader } from "../../components/ui/card";
import { Donut } from "../../components/ui/donut";
import { Drawer } from "../../components/ui/drawer";
import { Empty } from "../../components/ui/empty";
import { Input } from "../../components/ui/field";
import { FilterPill, Toolbar } from "../../components/ui/filter-pill";
import { Metric } from "../../components/ui/metric";
import { Pager } from "../../components/ui/pager";
import { Badge, Pill } from "../../components/ui/pill";
import { Table, type TableColumn } from "../../components/ui/table";
import { Updating } from "../../components/ui/updating";
import {
	payrollTile,
	PERSON_STATUS_LABELS,
	PERSON_STATUS_TONES,
	personStatus,
	UNSPECIFIED_DATE,
	upcomingTile,
} from "../../domain/people";
import { formatShortDate, initialsOf } from "../../lib/format";
import { showToast } from "../../lib/toast";
import { NoAccess } from "./-components/no-access";
import { peopleListSearchSchema, type PeopleListSearch } from "./-components/people-list-search";
import { PersonForm } from "./-components/person-form";

const peopleSearchSchema = peopleListSearchSchema.extend({
	/** "new" opens the drawer that adds a person. */
	person: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/people/")({
	validateSearch: peopleSearchSchema,
	// Everything in the address but the drawer that adds a person names the page of the list to read.
	loaderDeps: ({ search: { person: _adding, ...list } }) => list,
	loader: async ({ context, deps }) => {
		// A member with no access to People & HR is told so by the page; asking the API would only answer 403.
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session === null || session.member.access.people === "none") {
			return;
		}
		try {
			await Promise.all([
				context.queryClient.query({ ...peoplePageQueryOptions(deps), staleTime: "static" }),
				context.queryClient.query({ ...rolesAndTeamsQueryOptions(), staleTime: "static" }),
			]);
		} catch (error) {
			// A page link the API no longer takes, from an old bookmark or edited by hand: start at the first page of the
			// same list.
			if (deps.page !== undefined && error instanceof ApiError && error.status === 400) {
				throw redirect({ to: "/people", search: { ...deps, page: undefined }, replace: true });
			}
			throw error;
		}
	},
	head: () => ({ meta: [{ title: "People & HR · Ravnar" }] }),
	staticData: { crumbs: ["People & HR"] },
	component: PeoplePage,
});

/**
 * A person's name, linking to their own page with the list's page and team or role, which that page's Back link and
 * its removal go back to.
 */
function PersonNameLink({ person }: { readonly person: Person }) {
	// Everything in the list's address but the drawer that adds a person.
	const { person: _adding, ...list } = Route.useSearch();
	return (
		<Link
			to="/people/$personId"
			params={{ personId: person.id }}
			search={list}
			className="block font-medium text-ink hover:underline"
		>
			{person.name}
		</Link>
	);
}

const columns: readonly TableColumn<Person>[] = [
	{
		key: "name",
		header: "Person",
		cell: person => (
			<span className="flex items-center gap-2.5">
				<Avatar initials={initialsOf(person.name)} size="sm" />
				<span className="min-w-0">
					<PersonNameLink person={person} />
					<span className="block text-caption text-ink-3">{person.email}</span>
				</span>
			</span>
		),
	},
	{ key: "role", header: "Role", muted: true, cell: person => person.role },
	{ key: "team", header: "Team", cell: person => <Badge>{person.team}</Badge> },
	{
		key: "start",
		header: "Start date",
		align: "right",
		muted: true,
		cell: person => formatShortDate(person.startDate),
	},
	{
		key: "end",
		header: "End date",
		align: "right",
		muted: true,
		cell: person => (person.endDate === undefined ? UNSPECIFIED_DATE : formatShortDate(person.endDate)),
	},
	{
		key: "status",
		header: "Status",
		align: "right",
		cell: person => {
			const status = personStatus(person);
			return <Pill tone={PERSON_STATUS_TONES[status]}>{PERSON_STATUS_LABELS[status]}</Pill>;
		},
	},
];

/**
 * The group of a donut that `label` names — spelled any way, as the API matches it (`personLabelKey`) — under the
 * spelling the donut prints, or undefined when no group is named or none on the books holds it.
 */
function groupNamed(groups: readonly PersonGroup[], label: string | undefined): string | undefined {
	if (label === undefined) {
		return undefined;
	}
	const key = personLabelKey(label);
	return groups.find(group => personLabelKey(group.label) === key)?.label;
}

interface ShownGroupProps {
	/** "team" or "role", as the button's name says it. */
	readonly kind: "team" | "role";
	/** The team or the role the list shows. */
	readonly label: string;
	/** Shows every team, or every role, again. */
	readonly onClear: () => void;
}

/** Says which team or role the list shows, beside its count, and lets go of it. */
function ShownGroup({ kind, label, onClear }: ShownGroupProps) {
	return (
		<Button size="sm" variant="quiet" aria-label={`Show every ${kind}, not only ${label}`} onClick={onClear}>
			{kind === "team" ? "Team" : "Role"}: {label}
			<CloseIcon className="stroke-current" />
		</Button>
	);
}

/** What the list says when it holds nobody: why, as far as its address can tell. */
function emptyText({ team, role, status, q }: PeopleListSearch): string {
	if (status !== undefined || (q ?? "").trim() !== "") {
		return "Nobody matches.";
	}
	return team === undefined && role === undefined ? "No people yet." : "Nobody holds that team or role any longer.";
}

interface PeopleDirectoryProps {
	/** Only an editor of People & HR sees Add person and the drawer that opens it. */
	readonly canAdd: boolean;
}

function PeopleDirectory({ canAdd }: PeopleDirectoryProps) {
	const { person, ...list } = Route.useSearch();
	const { team, role, status, q } = list;
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const formId = useId();
	// What has been typed into the search box. The box keeps it rather than reading it back from the address: the API
	// answers the search, so the navigation a keystroke starts only arrives once the answer does, and a box fed from the
	// address would put the older text back over the newer — the Directory's search box learned the same. The list is
	// still read from the address alone, and the box starts from it, so a link with a `q` opens showing its search.
	const [typed, setTyped] = useState(q ?? "");
	const peopleQuery = peoplePageQueryOptions(list);
	const { data: peoplePage } = useSuspenseQuery(peopleQuery);
	const { data: rolesAndTeams } = useSuspenseQuery(rolesAndTeamsQueryOptions());
	const isUpdating = useIsFetching({ queryKey: peopleQuery.queryKey, exact: true }) > 0;
	const headcount = peoplePage.counts.employee + peoplePage.counts.contractor;
	const { total, distribution } = peoplePage;
	// The slices the list shows, as the donuts spell them; a team or role in the URL that nobody on the books holds any
	// longer chooses no slice, and the list comes back empty.
	const shownTeam = groupNamed(distribution.teams, team);
	const shownRole = groupNamed(distribution.roles, role);
	const ended = peoplePage.counts.ended;
	// The donuts count who is on the books, so they come to less than the table's heading; say so rather than let the
	// two numbers look like a bug.
	const leftNote =
		ended === 0 ? undefined : `Not counting ${ended} ${ended === 1 ? "person who has" : "people who have"} left.`;
	const isAdding = person === "new" && canAdd;
	const payroll = payrollTile(peoplePage.payroll);
	const upcoming = upcomingTile(peoplePage.upcoming);
	// The counts cover everyone, however the list is narrowed, so the pills keep saying how many there are of each
	// status while one of them is chosen.
	const everyone = PERSON_STATUSES.reduce((sum, option) => sum + peoplePage.counts[option], 0);

	// A new person changes the list, the roles and teams, the Directory that projects them, and the audit log, which
	// lives under "settings".
	const addMutation = useMutation({
		mutationFn: addPerson,
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["people"] }),
				queryClient.invalidateQueries({ queryKey: ["settings"] }),
				// A person is projected into the Directory, and the audit entry arrives through a queue: both land
				// after this answer did.
				askAgain(["directory"], ["settings", "audit-log"]),
			]),
	});

	/**
	 * Shows only the people of one team or one role, or everyone, as the donuts ask. One slice at a time, so choosing a
	 * team lets go of a role and the other way round. A page link names the list it was made for, so the page goes too.
	 */
	function show(filter: PeopleFilter) {
		void navigate({
			search: previous => ({ ...previous, team: filter.team, role: filter.role, page: undefined }),
			replace: true,
		});
	}

	/** Shows one status, or every status; a team or role and the search stay. The page goes, as `show` says. */
	function showStatus(option: PersonStatus | undefined) {
		void navigate({ search: previous => ({ ...previous, status: option, page: undefined }), replace: true });
	}

	const closeDrawer = () => {
		addMutation.reset();
		void navigate({ search: previous => ({ ...previous, person: undefined }), replace: true });
	};

	const savePerson = async (input: AddPersonRequest) => {
		try {
			await addMutation.mutateAsync(input);
		} catch {
			// The alert in the drawer shows addMutation.error.
			return;
		}
		closeDrawer();
		showToast(`${input.name} added to People & HR.`);
	};

	return (
		<>
			<PageHeader
				title="People & HR"
				description="Headcount, what it costs, and who is starting or leaving."
				actions={
					canAdd ? (
						<Button
							variant="primary"
							onClick={() => {
								void navigate({ search: previous => ({ ...previous, person: "new" }) });
							}}
						>
							Add person
						</Button>
					) : undefined
				}
			/>
			<div className="mb-[22px] grid grid-cols-1 gap-[11px] md:grid-cols-3">
				<Metric label="Headcount" value={headcount} sub={`${peoplePage.counts.starting} starting`} />
				<Metric label="Monthly payroll" value={payroll.value} sub={payroll.sub} />
				<Metric label="Coming up" value={upcoming.value} sub={upcoming.sub} tone={upcoming.tone} />
			</div>
			{distribution.total === 0 ? null : (
				<div className="mb-[22px] grid grid-cols-1 gap-[11px] md:grid-cols-2">
					<Card>
						<CardHeader title="Teams" />
						<Donut
							slices={distribution.teams}
							unit="people"
							note={leftNote}
							selected={shownTeam}
							selectLabel="Show one team"
							onSelect={label => {
								show({ team: label === shownTeam ? undefined : label });
							}}
						/>
					</Card>
					<Card>
						<CardHeader title="Roles" />
						<Donut
							slices={distribution.roles}
							unit="people"
							note={leftNote}
							selected={shownRole}
							selectLabel="Show one role"
							onSelect={label => {
								show({ role: label === shownRole ? undefined : label });
							}}
						/>
					</Card>
				</div>
			)}
			<Toolbar
				end={
					<div className="w-[200px]">
						<Input
							aria-label="Search people"
							placeholder="Search people"
							// As long a search as the API takes (`peopleQuerySchema.q`), so the box never asks it one it
							// answers with 400.
							maxLength={PEOPLE_QUERY_MAX_LENGTH}
							value={typed}
							onChange={event => {
								const text = event.target.value;
								setTyped(text);
								// A page link names the list it was made for, and every keystroke makes another list.
								void navigate({
									search: previous => ({
										...previous,
										q: text === "" ? undefined : text,
										page: undefined,
									}),
									replace: true,
								});
							}}
						/>
					</div>
				}
			>
				<FilterPill
					active={status === undefined}
					count={everyone}
					onClick={() => {
						showStatus(undefined);
					}}
				>
					All
				</FilterPill>
				{PERSON_STATUSES.map(option => (
					<FilterPill
						key={option}
						active={status === option}
						count={peoplePage.counts[option]}
						onClick={() => {
							showStatus(status === option ? undefined : option);
						}}
					>
						{PERSON_STATUS_LABELS[option]}
					</FilterPill>
				))}
			</Toolbar>
			<Card>
				<CardHeader
					title={`${total} ${total === 1 ? "person" : "people"}`}
					action={
						<div className="flex items-center gap-2">
							<Updating active={isUpdating} />
							{team === undefined ? null : (
								<ShownGroup
									kind="team"
									label={shownTeam ?? team}
									onClear={() => {
										show({ role });
									}}
								/>
							)}
							{role === undefined ? null : (
								<ShownGroup
									kind="role"
									label={shownRole ?? role}
									onClear={() => {
										show({ team });
									}}
								/>
							)}
						</div>
					}
				/>
				{total === 0 ? (
					<Empty>{emptyText(list)}</Empty>
				) : (
					<Table
						label="People"
						columns={columns}
						rows={peoplePage.people}
						rowKey={person => person.id}
						interactiveRows
					/>
				)}
				{peoplePage.next === null && peoplePage.previous === null ? null : (
					<Pager
						label="People pages"
						previous={
							peoplePage.previous === null ? null : (
								<Link
									to="/people"
									search={{ ...list, page: peoplePage.previous }}
									className={buttonClassName({ size: "sm" })}
								>
									Previous
								</Link>
							)
						}
						next={
							peoplePage.next === null ? null : (
								<Link
									to="/people"
									search={{ ...list, page: peoplePage.next }}
									className={buttonClassName({ size: "sm" })}
								>
									Next
								</Link>
							)
						}
					/>
				)}
			</Card>
			<Drawer
				open={isAdding}
				title="Add person"
				onClose={closeDrawer}
				footer={
					<>
						<Button onClick={closeDrawer}>Cancel</Button>
						<Button type="submit" form={formId} variant="primary" disabled={addMutation.isPending}>
							{addMutation.isPending ? "Adding…" : "Add person"}
						</Button>
					</>
				}
			>
				{isAdding ? (
					<div className="flex flex-col gap-4">
						<PersonForm formId={formId} rolesAndTeams={rolesAndTeams} onSave={savePerson} />
						{addMutation.error === null ? null : (
							<p role="alert" className="text-caption text-alert">
								{addMutation.error.message}
							</p>
						)}
					</div>
				) : null}
			</Drawer>
		</>
	);
}

function PeoplePage() {
	const { member } = useSession();
	return member.access.people === "none" ? (
		<NoAccess description="Headcount, what it costs, and who is starting or leaving." />
	) : (
		<PeopleDirectory canAdd={member.access.people === "editor"} />
	);
}
